"use client";

import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getAccountingConfig,
  getCostCentersForOrganization,
  type OrganizationConfig,
  type CostCenterConfig,
} from "@/lib/constants/accounting-config";
import { AccountingSystem } from "@/lib/constants/accounting";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export interface AccountingSettings {
  accounting_system: string | null;
  organization_id: string | null;
  cost_center: string | null;
  access_token?: string | null;
  client_id?: string | null;
  client_secret?: string | null;
  auto_invoice_enabled?: boolean;
}

interface AccountingSettingsSectionProps {
  value: AccountingSettings;
  onChange: (settings: AccountingSettings) => void;
  venueId?: number | string;
  accountingId?: number | null;
  /**
   * Show the Authorize / Re-authorize buttons. Connecting writes credentials
   * shared by every venue on the system, so only super admins can.
   */
  canConnect?: boolean;
}

interface AccountingSystemOption {
  id: number;
  name: string;
}

export default function AccountingSettingsSection({
  value,
  onChange,
  venueId,
  accountingId,
  canConnect = true,
}: AccountingSettingsSectionProps) {
  const [organizations, setOrganizations] = useState<OrganizationConfig[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterConfig[]>([]);
  const [savingAutoInvoice, setSavingAutoInvoice] = useState(false);

  // Just use hardcoded values for now - we can make it dynamic later
  const accountingSystems: AccountingSystemOption[] = [
    { id: 1, name: 'fortnox' },
    { id: 2, name: 'economic' },
    { id: 3, name: 'poweroffice' }
  ];

  // Initialize organizations based on selected system
  useEffect(() => {
    if (value.accounting_system && (value.accounting_system === 'fortnox' || value.accounting_system === 'economic' || value.accounting_system === 'poweroffice')) {
      const config = getAccountingConfig(
        value.accounting_system as "fortnox" | "economic" | "poweroffice",
      );
      setOrganizations(config.organizations);

      // Load cost centers if organization is selected
      if (value.organization_id) {
        const centers = getCostCentersForOrganization(
          value.accounting_system as "fortnox" | "economic" | "poweroffice",
          value.organization_id,
        );
        setCostCenters(centers);
      }
    } else {
      // Default to Fortnox organizations
      const fortnoxConfig = getAccountingConfig("fortnox");
      setOrganizations(fortnoxConfig.organizations);
    }
  }, [value.accounting_system, value.organization_id]);

  const handleSystemChange = (system: string) => {
    if (!system) {
      onChange({
        accounting_system: null,
        organization_id: null,
        cost_center: null,
      });
      return;
    }

    onChange({
      ...value,
      accounting_system: system,
      organization_id: null,
      cost_center: null,
    });
  };

  const handleOrganizationChange = (orgId: string) => {
    onChange({
      ...value,
      organization_id: orgId,
      cost_center: null,
    });
  };

  const handleCostCenterChange = (costCenterId: string) => {
    onChange({
      ...value,
      cost_center: costCenterId,
    });
  };

  // Auto-invoicing saves on its own PATCH rather than waiting for the venue
  // form's Save: the form only submits accounting settings once system,
  // organization and cost center are all filled in, and this switch has to
  // work independently of that.
  const handleAutoInvoiceChange = async (enabled: boolean) => {
    if (!venueId) return;

    setSavingAutoInvoice(true);
    // Optimistic — reverted below if the write fails.
    onChange({ ...value, auto_invoice_enabled: enabled });

    try {
      const response = await fetch(
        `/api/superadmin/venue/${venueId}/accounting-settings`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ auto_invoice_enabled: enabled }),
        },
      );

      const body = await response.json().catch(() => ({}));

      if (!response.ok || !body.success) {
        throw new Error(body.error || "Failed to save auto-invoicing setting");
      }

      toast.success(
        enabled
          ? "Automatic invoicing enabled for this venue"
          : "Automatic invoicing disabled for this venue",
      );
    } catch (error) {
      onChange({ ...value, auto_invoice_enabled: !enabled });
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to save auto-invoicing setting",
      );
    } finally {
      setSavingAutoInvoice(false);
    }
  };

  return (
    <div className="space-y-4 pt-4 border-t">
      {/* Accounting System Selection */}
      <div className="space-y-2">
        <Label htmlFor="accounting_system">Accounting System</Label>
        <Select
          value={value.accounting_system || ""}
          onValueChange={handleSystemChange}
        >
          <SelectTrigger id="accounting_system">
            <SelectValue placeholder="Select accounting system" />
          </SelectTrigger>
          <SelectContent>
            {accountingSystems.map((system) => (
              <SelectItem key={system.id} value={system.name}>
                {system.name === 'fortnox' && 'Fortnox'}
                {system.name === 'economic' && 'e-conomic'}
                {system.name === 'poweroffice' && 'PowerOffice Go'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Organization Selection - Only for Fortnox and e-conomic */}
      {value.accounting_system && value.accounting_system !== 'poweroffice' && (
        <div className="space-y-2">
          <Label htmlFor="organization">Organization</Label>
          <Select
            value={value.organization_id || ""}
            onValueChange={handleOrganizationChange}
          >
            <SelectTrigger id="organization">
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Cost Center Selection - Only for Fortnox and e-conomic */}
      {value.organization_id && value.accounting_system !== 'poweroffice' && (
        <div className="space-y-2">
          <Label htmlFor="cost_center">Cost Center</Label>
          <Select
            value={value.cost_center || ""}
            onValueChange={handleCostCenterChange}
          >
            <SelectTrigger id="cost_center">
              <SelectValue placeholder="Select cost center" />
            </SelectTrigger>
            <SelectContent>
              {costCenters.map((center) => (
                <SelectItem key={center.id} value={center.id}>
                  {center.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* OAuth Authorization / PowerOffice Configuration */}
      {value.accounting_system && accountingId && (
        <div className="pt-4 border-t">
          <div className="space-y-2">
            <Label>API Authorization</Label>
            {value.accounting_system === 'poweroffice' ? (
              // PowerOffice configuration message
              <div className="space-y-2">
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-700 font-medium">
                    ✅ PowerOffice Go Configuration
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    PowerOffice is configured via environment variables. No additional setup needed.
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    The system will automatically use the API keys configured in your deployment environment.
                  </p>
                </div>
              </div>
            ) : value.accounting_system === 'economic' ? (
              // e-conomic uses two static grant tokens (no OAuth redirect).
              // Like PowerOffice, they're supplied via environment variables.
              <div className="space-y-2">
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-700 font-medium">
                    ✅ e-conomic Configuration
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    e-conomic is configured via environment variables
                    (ECONOMIC_APP_SECRET_TOKEN and
                    ECONOMIC_AGREEMENT_GRANT_TOKEN). No additional setup needed.
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    These static grant tokens don&apos;t expire, so there is no
                    authorization step.
                  </p>
                </div>
              </div>
            ) : value.access_token ? (
              <div className="space-y-2">
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-700 font-medium">
                    ⚠️ Credentials Configured
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">
                    {value.accounting_system === "fortnox"
                      ? "Fortnox"
                      : "e-conomic"}{" "}
                    tokens are stored. Test connection to verify they're still
                    valid.
                  </p>
                  {value.accounting_system === "fortnox" && (
                    <p className="text-xs text-yellow-600 mt-1">
                      Re-authorize as a Fortnox system administrator to switch
                      to a service account, which never needs re-authorizing
                      due to inactivity.
                    </p>
                  )}
                </div>
                {canConnect ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    // Re-authorize to get fresh tokens
                    const authUrl = venueId
                      ? `/api/oauth/${value.accounting_system}/authorize?venueId=${venueId}`
                      : `/api/oauth/${value.accounting_system}/authorize`;
                    window.open(authUrl, "_blank", "width=800,height=600");
                  }}
                  className="w-full"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Re-authorize{" "}
                  {value.accounting_system === "fortnox"
                    ? "Fortnox"
                    : "e-conomic"}
                </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Ask a super admin to connect: the connection is shared by
                    every venue on this accounting system.
                  </p>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500">
                  Authorize the application to access your{" "}
                  {value.accounting_system === "fortnox"
                    ? "Fortnox"
                    : "e-conomic"}{" "}
                  account. You need to configure client_id and client_secret in
                  the database first.
                  {value.accounting_system === "fortnox" &&
                    " Sign in as a Fortnox system administrator so the connection is set up as a service account."}
                </p>
                {canConnect ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const authUrl = venueId
                      ? `/api/oauth/${value.accounting_system}/authorize?venueId=${venueId}`
                      : `/api/oauth/${value.accounting_system}/authorize`;
                    window.open(authUrl, "_blank", "width=800,height=600");
                  }}
                  className="w-full"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Authorize with{" "}
                  {value.accounting_system === "fortnox"
                    ? "Fortnox"
                    : "e-conomic"}
                </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Ask a super admin to connect: the connection is shared by
                    every venue on this accounting system.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Automatic invoicing — per-venue, saves immediately. Only offered for
          an existing venue, since the PATCH needs a venue id. */}
      {venueId && (
        <div className="pt-4 border-t">
          <div className="space-y-2">
            <Label>Automatic Invoicing</Label>
            <div className="flex items-start gap-3">
              <Checkbox
                id="auto_invoice_enabled"
                checked={value.auto_invoice_enabled ?? false}
                disabled={savingAutoInvoice}
                onCheckedChange={(checked) =>
                  handleAutoInvoiceChange(checked === true)
                }
              />
              <div className="space-y-1 leading-none">
                <label
                  htmlFor="auto_invoice_enabled"
                  className="text-sm font-medium cursor-pointer"
                >
                  Issue invoices automatically for this venue
                  {savingAutoInvoice && (
                    <Loader2 className="ml-2 inline h-3 w-3 animate-spin text-gray-400" />
                  )}
                </label>
                <p className="text-xs text-gray-500">
                  An hourly job invoices each booking 3 hours after its show
                  ends, when the booking is Confirmed or Completed, pays by
                  invoice, and isn&apos;t flagged for manual invoicing. Flagged
                  bookings and bookings that already have an invoice are always
                  skipped, and staff can still issue any invoice by hand from
                  the booking.
                </p>
                {value.auto_invoice_enabled &&
                  value.accounting_system !== AccountingSystem.FORTNOX &&
                  value.accounting_system !== AccountingSystem.POWEROFFICE && (
                    <p className="text-xs text-amber-600">
                      Nothing will be issued until this venue uses Fortnox or
                      PowerOffice Go — those are the only systems the invoice
                      job can post to.
                    </p>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
